import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTenant } from "../../../hooks/useTenant";
import MemberService from "../../../services/member_service";
import AttendanceService from "../../../services/attendance_service";
import GamificationService from "../../../services/gamification_service";

/**
 * useAttendanceModal
 * Hook de lógica de negócio para o modal de controle de presença.
 * 
 * STATUS DO FRONTEND:
 *   'present'  → Aluno marcado como presente (será ATTENDED no backend)
 *   'absent'   → Aluno marcado como ausente (será NO_SHOW no backend)  
 *   'BOOKED'   → Aluno matriculado mas chamada ainda não realizada
 * 
 * STATUS DO BACKEND (Firestore):
 *   'ATTENDED'  → Presente
 *   'NO_SHOW'   → Ausente
 *   'BOOKED'    → Agendado (chamada não realizada)
 */
export const useAttendanceModal = ({ isOpen, onClose, schedule, onAttendanceSaved }) => {
  const navigate = useNavigate();
  const { idTenant } = useTenant();

  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [justAddedId, setJustAddedId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSearchingExtra, setIsSearchingExtra] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [gamificationSettings, setGamificationSettings] = useState(null);
  const searchDebounceRef = useRef(null);

  // -- Mapeamento Backend → Frontend --
  const mapBackendToFrontend = (backendStatus) => {
    if (backendStatus === "ATTENDED") return "present";
    if (backendStatus === "NO_SHOW") return "absent";
    // Alunos agendados (BOOKED) ou sem status definido aparecerão como "present" 
    // por padrão. Como oldStatus no bd será "BOOKED", isDirty se tornará true.
    return "present"; 
  };

  // -- Mapeamento Frontend → Backend --
  const mapFrontendToBackend = (frontendStatus) => {
    if (frontendStatus === "present") return "ATTENDED";
    if (frontendStatus === "absent") return "NO_SHOW";
    return "BOOKED";
  };

  // -- Carrega os detalhes dos membros ao abrir o modal --
  useEffect(() => {
    if (isOpen && schedule?.attendances) {
      const fetchAttendanceDetails = async () => {
        setLoadingMembers(true);
        try {
          const attendances = schedule.attendances;

          if (!attendances.length) {
            setMembers([]);
            return;
          }

          const memberPromises = attendances.map(async (att) => {
            const memberDetails = await MemberService.getMemberById(idTenant, att.idMember);

            return {
              id: att.idMember,
              name: memberDetails?.fullName || att.memberName,
              photo: memberDetails?.photoUrl,
              memberStatus: memberDetails?.status,
              idMember: memberDetails?.idMember,
              enrollmentType: att.type,
              idEnrollment: att.idEnrollment,
              justification: att.justification || "",
              status: mapBackendToFrontend(att.status),
              initialFrontendStatus: mapBackendToFrontend(att.status),
              oldStatus: att.status, // Status original do backend para calcular diff
              oldJustification: att.justification || "",
            };
          });

          const resolvedMembers = await Promise.all(memberPromises);
          setMembers(resolvedMembers);
        } catch (error) {
          console.error("[useAttendanceModal] Erro ao carregar detalhes dos alunos:", error);
          toast.error("Erro ao carregar lista de alunos.");
        } finally {
          setLoadingMembers(false);
        }
      };

      fetchAttendanceDetails();

      // Carregar settings de gamificação para XP
      GamificationService.getSettings(idTenant)
        .then(settings => setGamificationSettings(settings))
        .catch(() => setGamificationSettings(null));
    } else if (!isOpen) {
      setMembers([]);
      setSearchText("");
      setSearchResults([]);
      setJustAddedId(null);
    }
  }, [isOpen, schedule, idTenant]);

  // -- Estatísticas de presença --
  const attendanceStats = useMemo(() => {
    const present = members.filter(c => c.status === "present").length;
    const absent = members.filter(c => c.status === "absent").length;
    return { present, absent, total: members.length };
  }, [members]);

  // -- Verifica se houve alterações em relação ao Back (para o botão salvar) --
  const isDirty = useMemo(() => {
    return members.some(c => {
      const currentBackendStatus = mapFrontendToBackend(c.status);
      return currentBackendStatus !== c.oldStatus || c.justification !== c.oldJustification;
    });
  }, [members]);

  // -- Verifica se o usuário alterou algo manualmente (para o botão fechar) --
  const hasManualChanges = useMemo(() => {
    return members.some(c => {
      return c.status !== c.initialFrontendStatus || c.justification !== c.oldJustification;
    });
  }, [members]);

  // -- Busca debounced de membros para adicionar à chamada --
  useEffect(() => {
    if (!isOpen) return;

    clearTimeout(searchDebounceRef.current);

    if (!searchText || searchText.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    searchDebounceRef.current = setTimeout(async () => {
      setIsSearchingExtra(true);
      try {
        const allMembers = await MemberService.getMembers(idTenant);
        const query = searchText.trim().toLowerCase();
        const alreadyInList = new Set(members.map(m => m.id));

        const filtered = allMembers
          .filter(m => !alreadyInList.has(m.id))
          .filter(m =>
            (m.fullName || "").toLowerCase().includes(query) ||
            String(m.memberNumber || "").includes(query) ||
            (m.cpf || "").replace(/\D/g, "").includes(query.replace(/\D/g, ""))
          )
          .slice(0, 8)
          .map(m => ({ id: m.id, name: m.fullName, memberNumber: m.memberNumber, photo: m.photoUrl, memberStatus: m.status }));

        setSearchResults(filtered);
      } catch (err) {
        console.error("[useAttendanceModal] Erro na busca de membros:", err);
        setSearchResults([]);
      } finally {
        setIsSearchingExtra(false);
      }
    }, 350);

    return () => clearTimeout(searchDebounceRef.current);
  }, [searchText, isOpen, idTenant, members]);

  // -- Loading por tipo --
  const isLoading = (type) => {
    if (type === "save") return isSaving;
    if (type === "addExtra") return isSearchingExtra;
    return false;
  };

  // -- Ações --
  const handleMarkAbsence = (id) => {
    setMembers(prev => prev.map(c => (c.id === id ? { ...c, status: "absent", justification: "" } : c)));
  };

  const handleMarkPresent = (id) => {
    setMembers(prev => prev.map(c => (c.id === id ? { ...c, status: "present", justification: "" } : c)));
  };

  const handleChangeJustification = (id, justification) => {
    setMembers(prev => prev.map(c => (c.id === id ? { ...c, justification } : c)));
  };

  const handleSelectSearchMember = useCallback((selectedMember) => {
    setMembers(prev => {
      if (prev.some(m => m.id === selectedMember.id)) return prev;
      return [
        ...prev,
        {
          id: selectedMember.id,
          name: selectedMember.name,
          photo: selectedMember.photo || null,
          memberStatus: selectedMember.memberStatus,
          idMember: selectedMember.memberNumber,
          enrollmentType: "extra",
          idEnrollment: null,
          justification: "",
          status: "present",
          initialFrontendStatus: "present",
          oldStatus: "BOOKED",
          oldJustification: "",
        },
      ];
    });
    setJustAddedId(selectedMember.id);
    setSearchText("");
    setSearchResults([]);
    setTimeout(() => setJustAddedId(null), 2500);
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await AttendanceService.saveSessionAttendance(idTenant, schedule, members, { uid: "admin" }, gamificationSettings);
      if (onAttendanceSaved) {
        await onAttendanceSaved();
      }
      toast.success("Lista de presença salva com sucesso!");
      onClose();
    } catch (error) {
      console.error("[useAttendanceModal] Erro ao salvar:", error);
      toast.error(error.message || "Erro inesperado ao salvar controle de presença.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasManualChanges) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  };

  const handleForceClose = () => {
    setShowConfirm(false);
    onClose();
  };

  const title = useMemo(() => {
    if (!schedule) return "Controle de presença";
    return `${schedule.activityName} · ${schedule.startTime} - ${schedule.endTime}`;
  }, [schedule]);

  const handleGoToProfile = (memberId) => {
    onClose();
    navigate(`/members/${memberId}`);
  };

  return {
    members,
    loadingMembers,
    searchText,
    searchResults,
    justAddedId,
    attendanceStats,
    isDirty,
    showConfirm,
    title,
    setSearchText,
    setShowConfirm,
    isLoading,
    handleSelectSearchMember,
    handleMarkAbsence,
    handleMarkPresent,
    handleChangeJustification,
    handleSave,
    handleClose,
    handleForceClose,
    handleGoToProfile,
  };
};
